import React, { useState, useEffect, useMemo, useRef } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { toast } from 'sonner';
import { X, ChevronRight, ChevronLeft, Loader2, CheckCircle2, XCircle, Percent } from 'lucide-react';

const STEPS = ['Filters', 'Date Range', 'Multiplier', 'Preview', 'Progress'];
const TERMINAL = ['completed', 'partial_failed', 'failed'];

const STATUS_LABEL = {
  queued: 'Queued — starting…',
  running: 'Updating rates & pushing to OTAs…',
  completed: 'Completed successfully',
  partial_failed: 'Completed with some failures',
  failed: 'Failed',
};

export const BulkRateUpdateModal = ({ open, onClose, apiBase }) => {
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ cities: [], states: [], pincodes: [], hotels: [] });
  const [city, setCity] = useState('');
  const [stateF, setStateF] = useState('');
  const [pincode, setPincode] = useState('');
  const [hotelSearch, setHotelSearch] = useState('');
  const [selectedHotels, setSelectedHotels] = useState({}); // {id: true}
  const [checkIn, setCheckIn] = useState('');
  const [checkOut, setCheckOut] = useState('');
  const [multiplier, setMultiplier] = useState(null);
  const [customMult, setCustomMult] = useState('');
  const [customRate, setCustomRate] = useState('');
  const [closeOut, setCloseOut] = useState(false);
  const [previewRows, setPreviewRows] = useState([]);
  const [jobId, setJobId] = useState(null);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    // reset + load filters
    setStep(0); setCity(''); setStateF(''); setPincode(''); setHotelSearch('');
    setCheckIn(''); setCheckOut(''); setMultiplier(null); setCustomMult(''); setCustomRate(''); setCloseOut(false);
    setPreviewRows([]); setJob(null); setJobId(null);
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${apiBase}/bulk-rate/filters`);
        setFilters(res.data || { cities: [], states: [], pincodes: [], hotels: [] });
        const all = {};
        (res.data?.hotels || []).forEach(h => { all[h.id] = true; });
        setSelectedHotels(all);
      } catch (e) {
        toast.error('Failed to load hotels');
      } finally { setLoading(false); }
      // Re-attach to a running job created earlier (survives modal close / refresh).
      try {
        const lr = await axios.get(`${apiBase}/bulk-rate/latest`);
        const lj = lr.data?.job;
        if (lj && ['queued', 'running'].includes(lj.status)) {
          setJob(lj); setJobId(lj.job_id); setStep(4);
        }
      } catch (e) { /* no prior job */ }
    })();
  }, [open, apiBase]);

  // Poll the job status while a job is active. The job runs server-side
  // independently of this modal — closing/refreshing never stops it.
  useEffect(() => {
    if (!jobId) return;
    let cancelled = false;
    const tick = async () => {
      try {
        const res = await axios.get(`${apiBase}/bulk-rate/status/${jobId}`);
        if (cancelled) return;
        setJob(res.data);
        if (TERMINAL.includes(res.data?.status)) {
          clearInterval(pollRef.current);
          pollRef.current = null;
        }
      } catch (e) { /* transient — keep polling */ }
    };
    tick();
    pollRef.current = setInterval(tick, 3000);
    return () => { cancelled = true; if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; } };
  }, [jobId, apiBase]);

  // Hotels matching the city/state/pincode filters.
  const matchingHotels = useMemo(() => (filters.hotels || []).filter(h =>
    (!city || h.city === city) && (!stateF || h.state === stateF) && (!pincode || String(h.pincode) === pincode)
  ), [filters.hotels, city, stateF, pincode]);

  // When filters change, default-select all matching hotels.
  useEffect(() => {
    if (!open) return;
    const sel = {};
    matchingHotels.forEach(h => { sel[h.id] = true; });
    setSelectedHotels(sel);
  }, [city, stateF, pincode]);

  const chosenIds = matchingHotels.filter(h => selectedHotels[h.id]).map(h => h.id);
  const allChecked = matchingHotels.length > 0 && matchingHotels.every(h => selectedHotels[h.id]);

  // Free-text name search — narrows only the rendered list, never the selection.
  const visibleHotels = useMemo(() => {
    const q = hotelSearch.trim().toLowerCase();
    if (!q) return matchingHotels;
    return matchingHotels.filter(h => (h.name || '').toLowerCase().includes(q));
  }, [matchingHotels, hotelSearch]);

  const canNext = () => {
    if (step === 0) return chosenIds.length > 0;
    if (step === 1) return checkIn && checkOut && checkIn <= checkOut;
    if (step === 2) return closeOut
      || (multiplier && Number(multiplier) > 0)
      || (customRate !== '' && Number(customRate) > 0);
    return true;
  };

  const loadPreview = async () => {
    if (closeOut) { setPreviewRows([]); return; }  // close-out has no rate preview
    setLoading(true);
    try {
      const body = { property_ids: chosenIds, check_in: checkIn, check_out: checkOut };
      if (multiplier && Number(multiplier) > 0) body.multiplier = Number(multiplier);
      if (customRate !== '' && Number(customRate) > 0) body.base_rate = Number(customRate);
      const res = await axios.post(`${apiBase}/bulk-rate/preview`, body);
      setPreviewRows(res.data?.rows || []);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Preview failed');
    } finally { setLoading(false); }
  };

  const execute = async () => {
    setLoading(true);
    try {
      const body = {
        property_ids: chosenIds, check_in: checkIn, check_out: checkOut,
        close_out: closeOut,
      };
      if (!closeOut) {
        if (multiplier && Number(multiplier) > 0) body.multiplier = Number(multiplier);
        if (customRate !== '' && Number(customRate) > 0) body.base_rate = Number(customRate);
      }
      const res = await axios.post(`${apiBase}/bulk-rate/execute`, body);
      const jid = res.data?.job_id;
      setJobId(jid);
      setJob({ status: res.data?.status || 'queued', processed: 0, total: res.data?.total || 0, succeeded: 0, failed: 0, results: [] });
      setStep(4);
      toast.success(closeOut ? 'Close Out queued — running in the background' : 'Bulk rate update queued — running in the background');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to queue update');
    } finally { setLoading(false); }
  };

  const goNext = async () => {
    if (step === 2) { await loadPreview(); setStep(3); return; }
    if (step === 3) { await execute(); return; }
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  if (!open) return null;

  const isTerminal = job && TERMINAL.includes(job.status);
  const pct = job && job.total > 0 ? Math.round((job.processed / job.total) * 100) : 0;

  return (
    <div className="fixed inset-0 z-[100] bg-slate-900/95 backdrop-blur-sm flex flex-col" data-testid="bulk-rate-modal">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-emerald-500/20 flex items-center justify-center">
            <Percent className="w-5 h-5 text-emerald-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-white">Bulk Rate Update</h2>
            <p className="text-xs text-slate-400">Rates only — inventory & availability are never changed</p>
          </div>
        </div>
        <button onClick={onClose} className="text-slate-400 hover:text-white p-2 rounded-lg hover:bg-white/10" data-testid="bulk-rate-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Stepper */}
      <div className="flex items-center gap-2 px-6 py-3 border-b border-white/10 overflow-x-auto">
        {STEPS.map((s, i) => (
          <div key={s} className="flex items-center gap-2 shrink-0">
            <div className={`w-6 h-6 rounded-full text-xs flex items-center justify-center font-semibold ${i === step ? 'bg-emerald-500 text-white' : i < step ? 'bg-emerald-500/40 text-white' : 'bg-white/10 text-slate-400'}`}>{i + 1}</div>
            <span className={`text-xs ${i === step ? 'text-white font-medium' : 'text-slate-400'}`}>{s}</span>
            {i < STEPS.length - 1 && <ChevronRight className="w-4 h-4 text-slate-600" />}
          </div>
        ))}
      </div>

      {/* Body */}
      <div className="flex-1 overflow-auto px-6 py-6">
        <div className="max-w-5xl mx-auto">
          {loading && (
            <div className="flex items-center justify-center py-10 text-slate-300" data-testid="bulk-rate-loading">
              <Loader2 className="w-6 h-6 animate-spin mr-2" /> Working…
            </div>
          )}

          {/* Step 1 — Filters */}
          {!loading && step === 0 && (
            <div className="space-y-5" data-testid="bulk-rate-step-filters">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">City</label>
                  <select value={city} onChange={e => setCity(e.target.value)} className="w-full h-9 rounded-md bg-white/5 border border-white/15 text-white text-sm px-2" data-testid="bulk-rate-filter-city">
                    <option value="">All cities</option>
                    {filters.cities.map(c => <option key={c} value={c} className="text-black">{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">State</label>
                  <select value={stateF} onChange={e => setStateF(e.target.value)} className="w-full h-9 rounded-md bg-white/5 border border-white/15 text-white text-sm px-2" data-testid="bulk-rate-filter-state">
                    <option value="">All states</option>
                    {filters.states.map(c => <option key={c} value={c} className="text-black">{c}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-300 mb-1">Pincode</label>
                  <select value={pincode} onChange={e => setPincode(e.target.value)} className="w-full h-9 rounded-md bg-white/5 border border-white/15 text-white text-sm px-2" data-testid="bulk-rate-filter-pincode">
                    <option value="">All pincodes</option>
                    {filters.pincodes.map(c => <option key={c} value={c} className="text-black">{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="rounded-lg border border-white/10 bg-white/5">
                <div className="flex items-center justify-between px-4 py-2 border-b border-white/10">
                  <span className="text-sm text-white font-medium">{matchingHotels.length} matching hotel(s) — {chosenIds.length} selected</span>
                  <label className="flex items-center gap-2 text-xs text-slate-300 cursor-pointer" data-testid="bulk-rate-hotel-toggle-all">
                    <input type="checkbox" checked={allChecked} onChange={e => {
                      const sel = { ...selectedHotels };
                      matchingHotels.forEach(h => { sel[h.id] = e.target.checked; });
                      setSelectedHotels(sel);
                    }} />
                    Select all
                  </label>
                </div>
                <div className="px-4 py-2 border-b border-white/10">
                  <Input
                    type="text"
                    value={hotelSearch}
                    onChange={e => setHotelSearch(e.target.value)}
                    placeholder="Search hotels by name…"
                    className="bg-white/5 border-white/15 text-white placeholder:text-slate-500 h-9"
                    data-testid="bulk-rate-hotel-search"
                  />
                </div>
                <div className="max-h-[42vh] overflow-auto divide-y divide-white/5">
                  {matchingHotels.length === 0 && <div className="px-4 py-6 text-sm text-slate-400 text-center">No hotels match these filters.</div>}
                  {matchingHotels.length > 0 && visibleHotels.length === 0 && <div className="px-4 py-6 text-sm text-slate-400 text-center" data-testid="bulk-rate-hotel-search-empty">No hotels match "{hotelSearch}".</div>}
                  {visibleHotels.map(h => (
                    <label key={h.id} className="flex items-center gap-3 px-4 py-2.5 hover:bg-white/5 cursor-pointer" data-testid={`bulk-rate-hotel-${h.id}`}>
                      <input type="checkbox" checked={!!selectedHotels[h.id]} onChange={e => setSelectedHotels(prev => ({ ...prev, [h.id]: e.target.checked }))} />
                      <div className="flex-1">
                        <div className="text-sm text-white">{h.name}</div>
                        <div className="text-[11px] text-slate-400">{[h.city, h.state, h.pincode].filter(Boolean).join(' • ')}</div>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            </div>
          )}

          {/* Step 2 — Date Range */}
          {!loading && step === 1 && (
            <div className="space-y-4 max-w-md" data-testid="bulk-rate-step-dates">
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Check-in</label>
                <Input type="date" value={checkIn} onChange={e => setCheckIn(e.target.value)} className="bg-white/5 border-white/15 text-white" data-testid="bulk-rate-checkin" />
              </div>
              <div>
                <label className="block text-xs font-medium text-slate-300 mb-1">Check-out</label>
                <Input type="date" value={checkOut} min={checkIn || undefined} onChange={e => setCheckOut(e.target.value)} className="bg-white/5 border-white/15 text-white" data-testid="bulk-rate-checkout" />
              </div>
              {checkIn && checkOut && checkIn > checkOut && <p className="text-xs text-red-400">Check-out must be on or after check-in.</p>}
            </div>
          )}

          {/* Step 3 — Multiplier */}
          {!loading && step === 2 && (
            <div className="space-y-5 max-w-lg" data-testid="bulk-rate-step-multiplier">
              {/* Close Out toggle — when on, this run is a stop-sell close-out
                  instead of a rate multiplier. */}
              <div className="flex items-center justify-between rounded-lg border border-white/15 bg-white/5 px-4 py-3">
                <div>
                  <p className="text-sm font-semibold text-white">Close Out</p>
                  <p className="text-xs text-slate-400">Mark the selected dates as closed out (stop-sell) and push to all OTAs.</p>
                </div>
                <button
                  type="button"
                  onClick={() => setCloseOut(v => !v)}
                  role="switch"
                  aria-checked={closeOut}
                  data-testid="bulk-rate-closeout-toggle"
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${closeOut ? 'bg-rose-500' : 'bg-white/20'}`}
                >
                  <span className={`inline-block h-5 w-5 transform rounded-full bg-white transition-transform ${closeOut ? 'translate-x-5' : 'translate-x-0.5'}`} />
                </button>
              </div>

              {closeOut ? (
                <p className="text-sm text-rose-300" data-testid="bulk-rate-closeout-note">
                  Close Out selected — rates will not change. The selected dates will be marked closed
                  for all room types of the chosen hotel(s) and pushed to every connected OTA.
                </p>
              ) : (
                <>
                  <p className="text-sm text-slate-300">
                    New rate = <strong>Rate</strong> and/or <strong>multiplier</strong>. Enter a rate, a multiplier, or both
                    (both ⇒ Rate × multiplier). Zero rates are included.
                  </p>
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Rate (₹)</label>
                    <Input type="number" step="0.01" min="0" value={customRate}
                      onChange={e => setCustomRate(e.target.value)}
                      placeholder="e.g. 2500" className="bg-white/5 border-white/15 text-white max-w-[200px]" data-testid="bulk-rate-custom-rate" />
                    <p className="text-[11px] text-slate-400 mt-1">Absolute rate value applied to every selected rate plan.</p>
                  </div>
                  <div className="flex flex-wrap gap-3">
                    {[1.5, 2, 0.5].map(m => (
                      <button key={m} onClick={() => { setMultiplier(m); setCustomMult(''); }}
                        className={`px-5 py-3 rounded-lg border text-sm font-semibold ${Number(multiplier) === m && !customMult ? 'bg-emerald-500 border-emerald-500 text-white' : 'bg-white/5 border-white/15 text-slate-200 hover:bg-white/10'}`}
                        data-testid={`bulk-rate-mult-${m}`}>{m}×</button>
                    ))}
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-300 mb-1">Custom multiplier</label>
                    <Input type="number" step="0.01" min="0.01" value={customMult}
                      onChange={e => { setCustomMult(e.target.value); setMultiplier(e.target.value ? Number(e.target.value) : null); }}
                      placeholder="e.g. 1.25" className="bg-white/5 border-white/15 text-white max-w-[200px]" data-testid="bulk-rate-mult-custom" />
                  </div>
                  {(customRate !== '' && Number(customRate) > 0) && (
                    <p className="text-sm text-emerald-400" data-testid="bulk-rate-selected-rate">Rate: ₹{Number(customRate)}{multiplier > 0 ? ` × ${multiplier}` : ''}</p>
                  )}
                  {multiplier > 0 && !(customRate !== '' && Number(customRate) > 0) && <p className="text-sm text-emerald-400">Selected multiplier: {multiplier}×</p>}
                </>
              )}
            </div>
          )}

          {/* Step 4 — Preview */}
          {!loading && step === 3 && (
            <div className="space-y-3" data-testid="bulk-rate-step-preview">
              {closeOut ? (
                <p className="text-sm text-rose-300" data-testid="bulk-rate-closeout-confirm">
                  You are about to <strong>CLOSE OUT</strong> {chosenIds.length} hotel(s) for {checkIn} → {checkOut}.
                  All room types will be marked closed for these dates and the close-out will be pushed to all connected OTAs (Agoda, Yatra, MMT).
                </p>
              ) : (
              <p className="text-sm text-slate-300">{previewRows.length} rate row(s) will be updated across {chosenIds.length} hotel(s), {checkIn} → {checkOut}
                {customRate !== '' && Number(customRate) > 0 ? `, ₹${Number(customRate)}` : ''}
                {multiplier > 0 ? ` ×${multiplier}` : ''}.</p>
              )}
              {!closeOut && (
              <div className="rounded-lg border border-white/10 overflow-auto max-h-[55vh]">
                <table className="w-full text-sm" data-testid="bulk-rate-preview-table">
                  <thead className="bg-white/10 text-slate-200 sticky top-0">
                    <tr>
                      <th className="text-left px-3 py-2 font-medium">Hotel</th>
                      <th className="text-left px-3 py-2 font-medium">Room Type</th>
                      <th className="text-left px-3 py-2 font-medium">Rate Plan</th>
                      <th className="text-right px-3 py-2 font-medium">Occ</th>
                      <th className="text-right px-3 py-2 font-medium">Current</th>
                      <th className="text-right px-3 py-2 font-medium">New</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-200">
                    {previewRows.map((r, i) => (
                      <tr key={i} className="border-t border-white/5" data-testid={`bulk-rate-preview-row-${i}`}>
                        <td className="px-3 py-1.5">{r.hotel_name}</td>
                        <td className="px-3 py-1.5">{r.room_type_name}</td>
                        <td className="px-3 py-1.5">{r.rate_plan_name || r.rate_plan_code}</td>
                        <td className="px-3 py-1.5 text-right">{r.occupancy}</td>
                        <td className="px-3 py-1.5 text-right">{r.current_rate}</td>
                        <td className="px-3 py-1.5 text-right text-emerald-400 font-medium">{r.new_rate}</td>
                      </tr>
                    ))}
                    {previewRows.length === 0 && <tr><td colSpan={6} className="px-3 py-6 text-center text-slate-400">No rate rows found for this selection.</td></tr>}
                  </tbody>
                </table>
              </div>
              )}
            </div>
          )}

          {/* Step 5 — Progress / Results */}
          {step === 4 && job && (
            <div className="space-y-5" data-testid="bulk-rate-progress">
              {/* Status banner */}
              <div className={`flex items-center gap-3 p-4 rounded-lg border ${
                job.status === 'completed' ? 'bg-emerald-500/15 border-emerald-500/30'
                : job.status === 'failed' ? 'bg-red-500/15 border-red-500/30'
                : job.status === 'partial_failed' ? 'bg-amber-500/15 border-amber-500/30'
                : 'bg-sky-500/15 border-sky-500/30'}`} data-testid="bulk-rate-status-banner">
                {isTerminal
                  ? (job.status === 'completed'
                      ? <CheckCircle2 className="w-6 h-6 text-emerald-400" />
                      : <XCircle className={`w-6 h-6 ${job.status === 'failed' ? 'text-red-400' : 'text-amber-400'}`} />)
                  : <Loader2 className="w-6 h-6 text-sky-400 animate-spin" />}
                <div className="text-white text-sm">
                  <div className="font-semibold" data-testid="bulk-rate-status-label">{STATUS_LABEL[job.status] || job.status}</div>
                  <div className="text-slate-300">Reusing the existing MMT, Agoda & Yatra push — inventory untouched.</div>
                </div>
              </div>

              {/* Progress bar */}
              <div data-testid="bulk-rate-progress-bar">
                <div className="flex items-center justify-between text-xs text-slate-300 mb-1">
                  <span>Overall progress</span>
                  <span data-testid="bulk-rate-progress-count">{job.processed} / {job.total} ({pct}%)</span>
                </div>
                <div className="w-full h-3 rounded-full bg-white/10 overflow-hidden">
                  <div className={`h-full transition-all duration-500 ${job.status === 'failed' ? 'bg-red-500' : job.status === 'partial_failed' ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${pct}%` }} />
                </div>
              </div>

              {/* Counts */}
              <div className="grid grid-cols-3 gap-3" data-testid="bulk-rate-counts">
                <div className="rounded-lg border border-white/10 bg-white/5 p-3 text-center">
                  <div className="text-2xl font-bold text-white" data-testid="bulk-rate-count-total">{job.total}</div>
                  <div className="text-xs text-slate-400 mt-0.5">Total units</div>
                </div>
                <div className="rounded-lg border border-emerald-500/20 bg-emerald-500/10 p-3 text-center">
                  <div className="text-2xl font-bold text-emerald-400" data-testid="bulk-rate-count-succeeded">{job.succeeded}</div>
                  <div className="text-xs text-slate-400 mt-0.5">Succeeded</div>
                </div>
                <div className="rounded-lg border border-red-500/20 bg-red-500/10 p-3 text-center">
                  <div className="text-2xl font-bold text-red-400" data-testid="bulk-rate-count-failed">{job.failed}</div>
                  <div className="text-xs text-slate-400 mt-0.5">Failed</div>
                </div>
              </div>

              {/* Currently processing */}
              {!isTerminal && (job.current_property_name || job.current_room_type_name) && (
                <div className="flex items-center gap-2 text-sm text-slate-300 px-1" data-testid="bulk-rate-current">
                  <Loader2 className="w-4 h-4 animate-spin text-sky-400" />
                  <span>Processing <span className="text-white font-medium">{job.current_property_name || '—'}</span>
                    {job.current_room_type_name ? <> · <span className="text-white">{job.current_room_type_name}</span></> : null}</span>
                </div>
              )}

              {!isTerminal && (
                <p className="text-xs text-slate-400 px-1">
                  You can safely close this window — the update keeps running in the background and you can reopen Bulk Rate Update to check its status.
                </p>
              )}

              {/* Per-property results */}
              {(job.results || []).length > 0 && (
                <div className="rounded-lg border border-white/10 overflow-auto max-h-[40vh]">
                  <table className="w-full text-sm" data-testid="bulk-rate-results-table">
                    <thead className="bg-white/10 text-slate-200 sticky top-0"><tr>
                      <th className="text-left px-3 py-2 font-medium">Hotel</th>
                      <th className="text-left px-3 py-2 font-medium">Room Type</th>
                      <th className="text-left px-3 py-2 font-medium">Status</th>
                    </tr></thead>
                    <tbody className="text-slate-200">
                      {(job.results || []).map((r, i) => (
                        <tr key={i} className="border-t border-white/5" data-testid={`bulk-rate-result-row-${i}`}>
                          <td className="px-3 py-1.5">{r.hotel_name}</td>
                          <td className="px-3 py-1.5">{r.room_type_name || r.room_type_id?.slice(0, 8)}</td>
                          <td className="px-3 py-1.5">
                            {r.status === 'success'
                              ? <span className="text-emerald-400">✓ success</span>
                              : <span className="text-red-400">✕ {r.error || 'failed'}</span>}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Footer */}
      <div className="flex items-center justify-between px-6 py-4 border-t border-white/10">
        <Button variant="outline" onClick={() => step === 0 || step === 4 ? onClose() : setStep(s => s - 1)}
          className="bg-transparent border-white/20 text-white hover:bg-white/10" data-testid="bulk-rate-back">
          {step === 0 || step === 4 ? 'Close' : <><ChevronLeft className="w-4 h-4 mr-1" />Back</>}
        </Button>
        {step < 3 && (
          <Button onClick={goNext} disabled={!canNext() || loading} className="bg-emerald-500 hover:bg-emerald-600 text-white" data-testid="bulk-rate-next">
            Next <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        )}
        {step === 3 && (
          <Button onClick={goNext} disabled={loading || (!closeOut && previewRows.length === 0)} className={`text-white ${closeOut ? 'bg-rose-500 hover:bg-rose-600' : 'bg-emerald-500 hover:bg-emerald-600'}`} data-testid="bulk-rate-confirm">
            {closeOut ? 'Confirm & Close Out' : 'Confirm & Update Rates'}
          </Button>
        )}
        {step === 4 && (
          <Button onClick={onClose} disabled={!isTerminal}
            className="bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50" data-testid="bulk-rate-done">
            {isTerminal ? 'Done' : 'Running…'}
          </Button>
        )}
      </div>
    </div>
  );
};

export default BulkRateUpdateModal;
